import './index.scss'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import PageTabs from '@components/PageTabs'
import PersonCard from '@components/PersonCard'
import usePeople, { usePeopleSearch } from '@hooks/usePeople'
import usePeopleImport from '@hooks/usePeopleImport'
import PeopleInfoModal from './InfoModal'
import { RoutedPerson, personPagePath } from './PersonPage'
import PeopleImportCard from './PeopleImportCard'
import { Person } from '@interfaces/person.interface'

import peopleIcon from 'url:@assets/icons/people.svg';

const VIEW_FADE_MS = 180;

export default function PeoplePage() {
  const [query, setQuery] = useState('');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const navigate = useNavigate();

  const openPerson = (person: Person) => {
    const state: RoutedPerson = { personName: person.attributes.name, backLocation: '/people' };
    navigate(personPagePath(person.id), { state });
  };
  const isSearching = query.trim().length > 0;

  const allPeople = usePeople();
  const searchResults = usePeopleSearch(query);

  // Newly imported people appear in the list as soon as the import finishes.
  const peopleImport = usePeopleImport({
    onCompleted: () => {
      void allPeople.refetch();
    }
  });

  // The outgoing view fades before the incoming one mounts, so only one list is
  // ever measured for height.
  const targetView = isSearching ? 'search' : 'default';
  const [visibleView, setVisibleView] = useState<'default' | 'search'>(targetView);

  useEffect(() => {
    if (visibleView === targetView) return;

    const timeout = window.setTimeout(() => setVisibleView(targetView), VIEW_FADE_MS);

    return () => window.clearTimeout(timeout);
  }, [targetView, visibleView]);

  const isLeaving = visibleView !== targetView;

  return (
    <div className='people-page'>
      <PeopleInfoModal
        isOpen={isInfoModalOpen}
        close={() => setIsInfoModalOpen(false)}
      />

      <PageTabs
        title='People'
        tabs={[]}
        buttons={[
          {
            label: "Info",
            icon: 'info',
            onClick: () => setIsInfoModalOpen(true)
          },
          {
            label: "New Person",
            icon: 'add',
            onClick: () => {

            }
          }
        ]} />

      <div className='people-search'>
        <input
          type='search'
          value={query}
          placeholder='Search by first name or email'
          aria-label='Search people'
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <PeopleImportCard
        state={peopleImport.state}
        providerName={peopleImport.providerName}
        canStart={peopleImport.canStart}
        isStarting={peopleImport.isStarting}
        error={peopleImport.error}
        onStart={() => {
          void peopleImport.start();
        }}
        onDismiss={peopleImport.dismiss}
      />

      <div className={isLeaving ? 'people-view is-leaving' : 'people-view'}>
        {visibleView === 'search' ? <PeopleResults
          people={searchResults.people}
          loading={searchResults.loading}
          hasLoaded={searchResults.hasLoaded}
          error={searchResults.error}
          hasMore={searchResults.hasMore}
          isLoadingMore={searchResults.isLoadingMore}
          loadMore={searchResults.loadMore}
          refetch={searchResults.refetch}
          onSelect={openPerson}
          emptyTitle='No Matches'
          emptyMessage={`No one found matching that ${searchResults.field === 'email' ? 'email' : 'name'}.${searchResults.field === 'name' ? ' If you are trying to find someone by email, type their full email address up to the @ symbol for better results.' : '' }`}
        /> : <PeopleResults
          people={allPeople.people}
          loading={allPeople.loading}
          hasLoaded={allPeople.hasLoaded}
          error={allPeople.error}
          hasMore={allPeople.hasMore}
          isLoadingMore={allPeople.isLoadingMore}
          loadMore={allPeople.loadMore}
          refetch={allPeople.refetch}
          onSelect={openPerson}
          emptyTitle='No People Yet'
          emptyMessage='People added to this church will show up here. Create one to get started.'
        />}
      </div>
    </div>
  )
}

function PeopleResults(props: {
  people: Person[];
  loading: boolean;
  hasLoaded: boolean;
  error: string | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  onSelect: (person: Person) => void;
  emptyTitle: string;
  emptyMessage: string;
}) {
  if (props.loading && props.people.length === 0) {
    return <div className='people-state'>
      <LoadingIndicator fullScreen={false} />
    </div>
  }

  if (props.error && props.people.length === 0) {
    return <ErrorView
      error={props.error}
      buttonText='Retry'
      buttonAction={props.refetch}
      fullHeight />
  }

  if (props.hasLoaded && props.people.length === 0) {
    return <ErrorView
      icon={peopleIcon}
      title={props.emptyTitle}
      error={props.emptyMessage}
      showButton={false}
      fullHeight />
  }

  return <>
    <div className='people'>
      {props.people.map((person) => {
        return <PersonCard
          key={person.id}
          name={person.attributes.name}
          email={person.attributes.email ?? undefined}
          onClick={() => props.onSelect(person)}
        />
      })}
    </div>

    {props.error ? <p className='people-error'>{props.error}</p> : null}

    {props.hasMore ? <div className='people-load-more'>
      <button type='button' disabled={props.isLoadingMore} onClick={props.loadMore}>
        {props.isLoadingMore ? 'Loading…' : 'Load More'}
      </button>
    </div> : null}
  </>
}
